/**
 * AdminPage — the tabbed Admin console (FND-007, ADH-001/004/006/008/009).
 *
 * Consolidates the configuration surfaces a staff admin manages:
 *   - Rules         — configurable rule sets + explainable simulation (FND-007,
 *                     ADH-008). Reuses the existing useRules hooks.
 *   - Employers     — employer master data: add / edit / remove (ADH-001).
 *   - State Directory — state contact directory (ADH-006).
 *   - PDF Mapping   — state PDF field-mapping definitions (ADH-004, INT-008).
 *   - Email Templates — message templates with usage stats (ADH-009).
 *
 * Rules data via useRules(); the catalog tabs via useAdminCatalog(); templates
 * via useNotifications(). Each surface is a faithful-but-lighter table + primary
 * action, degrading gracefully to a seed/empty state when its endpoint 404s.
 */
import { useMemo, useState, type CSSProperties } from 'react';
import { PageHeader } from '../components/PageHeader';
import { Card } from '../components/Card';
import { DataTable, type Column } from '../components/DataTable';
import { StatusBadge } from '../components/StatusBadge';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { Toolbar } from '../components/Toolbar';
import { useShellSlot } from '../components/shellContext';
import { colors, radius } from '../theme';
import { useRuleSets, useRuleSimulation, type RuleSet } from '../hooks/useRules';
import {
  useAdminCatalog,
  type AdminEmployer,
  type EmployerInput,
  type PdfMapping,
  type StateContact,
  type StateContactInput,
} from '../hooks/useAdminCatalog';
import { useNotifications, type MessageTemplate } from '../hooks/useNotifications';

type Tab = 'rules' | 'employers' | 'states' | 'pdf' | 'templates';

const TABS: { key: Tab; label: string }[] = [
  { key: 'rules', label: 'Rules' },
  { key: 'employers', label: 'Employers' },
  { key: 'states', label: 'State Directory' },
  { key: 'pdf', label: 'PDF Field Mapping' },
  { key: 'templates', label: 'Email Templates' },
];

const DEFAULT_CONTEXT = JSON.stringify(
  { charge_level: 3, days_since_admission: 12, state: 'TX' },
  null,
  2
);

export function AdminPage() {
  useShellSlot({ breadcrumb: 'Admin' });
  const [tab, setTab] = useState<Tab>('rules');

  return (
    <>
      <PageHeader
        title="Admin Console"
        subtitle="Configure the rules, master data, and templates that drive the platform. AI-assisted simulations are dry-runs and are never logged (FND-007)."
      />
      <Toolbar style={{ marginBottom: 16 }}>
        <div style={styles.tabs}>
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              style={{ ...styles.tabBtn, ...(tab === t.key ? styles.tabBtnActive : null) }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </Toolbar>

      {tab === 'rules' && <RulesTab />}
      {tab === 'employers' && <EmployersTab />}
      {tab === 'states' && <StatesTab />}
      {tab === 'pdf' && <PdfTab />}
      {tab === 'templates' && <TemplatesTab />}
    </>
  );
}

// --- Rules (FND-007, ADH-008) ----------------------------------------------

function RulesTab() {
  const { ruleSets, isLoading, error, refresh } = useRuleSets();
  const sim = useRuleSimulation();
  const [selected, setSelected] = useState<RuleSet | null>(null);
  const [version, setVersion] = useState(1);
  const [contextText, setContextText] = useState(DEFAULT_CONTEXT);
  const [parseError, setParseError] = useState<string | null>(null);

  const decisionEntries = useMemo(
    () => (sim.result ? Object.entries(sim.result.decision) : []),
    [sim.result]
  );

  const handleSelect = (rs: RuleSet) => {
    setSelected(rs);
    setVersion(rs.active_version);
    sim.clear();
  };

  const handleSimulate = async () => {
    if (!selected) return;
    setParseError(null);
    let context: Record<string, unknown>;
    try {
      const parsed = JSON.parse(contextText) as unknown;
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        throw new Error('Context must be a JSON object.');
      }
      context = parsed as Record<string, unknown>;
    } catch (e: unknown) {
      setParseError(e instanceof Error ? e.message : 'Invalid JSON.');
      return;
    }
    await sim.simulate(selected.rule_set_id, { context, version });
  };

  const columns: Column<RuleSet>[] = [
    {
      key: 'name',
      header: 'Rule set',
      render: (rs) => (
        <div>
          <div style={{ fontWeight: 600 }}>{rs.name}</div>
          {rs.description && <div style={styles.muted}>{rs.description}</div>}
        </div>
      ),
    },
    { key: 'type', header: 'Decision type', render: (rs) => rs.decision_type.replace(/_/g, ' ') },
    { key: 'version', header: 'Active', align: 'right', render: (rs) => `v${rs.active_version}` },
    {
      key: 'action',
      header: '',
      align: 'right',
      render: (rs) => (
        <button type="button" style={styles.smallBtn} onClick={() => handleSelect(rs)}>
          Simulate
        </button>
      ),
    },
  ];

  return (
    <>
      <Card
        title="Rule sets"
        actions={
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        }
        padded={false}
        style={{ marginBottom: 20 }}
      >
        {isLoading ? (
          <div style={{ padding: 24 }}>
            <LoadingIndicator text="Loading rule sets…" />
          </div>
        ) : error ? (
          <p style={{ ...styles.error, padding: 18 }} role="alert">
            {error.message}
          </p>
        ) : (
          <DataTable
            columns={columns}
            rows={ruleSets}
            rowKey={(rs) => rs.rule_set_id}
            emptyTitle="No rule sets configured"
          />
        )}
      </Card>

      <Card title="Simulate rule (dry-run)">
        {!selected ? (
          <p style={styles.muted}>Choose a rule set above to simulate.</p>
        ) : (
          <div style={styles.simBody}>
            <div style={styles.simControls}>
              <div style={styles.field}>
                <span style={styles.label}>Rule set</span>
                <div style={{ fontWeight: 600 }}>{selected.name}</div>
              </div>
              <div style={styles.field}>
                <span style={styles.label}>Version</span>
                <input
                  type="number"
                  min={1}
                  value={version}
                  onChange={(e) => setVersion(Math.max(1, Number(e.target.value) || 1))}
                  style={{ ...styles.input, width: 100 }}
                />
              </div>
              <div style={styles.field}>
                <span style={styles.label}>Input facts (JSON)</span>
                <textarea
                  value={contextText}
                  onChange={(e) => setContextText(e.target.value)}
                  rows={8}
                  spellCheck={false}
                  style={styles.textarea}
                />
              </div>
              {parseError && <p style={styles.error}>{parseError}</p>}
              {sim.error && <p style={styles.error}>{sim.error.message}</p>}
              <button type="button" onClick={handleSimulate} disabled={sim.isSimulating} style={styles.primaryBtn}>
                Run simulation
              </button>
              {sim.isSimulating && <LoadingIndicator text="Simulating…" />}
            </div>
            <div>
              <h4 style={styles.subLabel}>Result</h4>
              {!sim.result ? (
                <p style={styles.muted}>Run a simulation to see the explainable output.</p>
              ) : (
                <>
                  <p style={styles.muted}>
                    Applied v{sim.result.rule_version} · {sim.result.decision_type.replace(/_/g, ' ')}
                  </p>
                  <dl style={styles.decisionGrid}>
                    {decisionEntries.map(([k, v]) => (
                      <div key={k} style={styles.decisionRow}>
                        <dt style={styles.dt}>{k}</dt>
                        <dd style={styles.dd}>{String(v)}</dd>
                      </div>
                    ))}
                  </dl>
                  {sim.result.explanation && <p style={styles.explanation}>{sim.result.explanation}</p>}
                  {sim.result.factor_breakdown.length > 0 && (
                    <table style={styles.miniTable}>
                      <thead>
                        <tr>
                          <th style={styles.miniTh}>Factor</th>
                          <th style={{ ...styles.miniTh, textAlign: 'right' }}>Weight</th>
                          <th style={{ ...styles.miniTh, textAlign: 'right' }}>Contribution</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sim.result.factor_breakdown.map((f) => (
                          <tr key={f.name}>
                            <td style={styles.miniTd}>{f.label}</td>
                            <td style={{ ...styles.miniTd, textAlign: 'right' }}>{f.weight}</td>
                            <td style={{ ...styles.miniTd, textAlign: 'right' }}>{f.contribution}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

// --- Employers (ADH-001) ----------------------------------------------------

function EmployersTab() {
  const { employers, employersAvailable, isLoading, isWorking, actionError, refresh, addEmployer, removeEmployer } =
    useAdminCatalog();
  const [showAdd, setShowAdd] = useState(false);

  const columns: Column<AdminEmployer>[] = [
    { key: 'name', header: 'Employer', render: (e) => <span style={{ fontWeight: 600 }}>{e.name}</span> },
    { key: 'ein', header: 'EIN', render: (e) => <span style={styles.mono}>{e.ein ?? '—'}</span> },
    { key: 'state', header: 'State', render: (e) => e.state ?? '—' },
    {
      key: 'esi',
      header: 'ESI',
      render: (e) =>
        e.offers_esi ? <StatusBadge value="offers ESI" status="success" /> : <span style={styles.muted}>—</span>,
    },
    { key: 'status', header: 'Status', render: (e) => <StatusBadge value={e.status ?? 'active'} /> },
    {
      key: 'action',
      header: '',
      align: 'right',
      render: (e) => (
        <button type="button" style={styles.dangerBtn} disabled={isWorking} onClick={() => removeEmployer(e.employer_id)}>
          Remove
        </button>
      ),
    },
  ];

  return (
    <Card
      title="Employers"
      actions={
        <>
          {!employersAvailable && <span style={styles.seedTag}>seed</span>}
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
          <button type="button" style={styles.primaryBtnSm} onClick={() => setShowAdd(true)}>
            + Add employer
          </button>
        </>
      }
      padded={false}
    >
      <DataTable columns={columns} rows={employers} rowKey={(e) => e.employer_id} emptyTitle="No employers" />
      {showAdd && (
        <EmployerDialog
          isWorking={isWorking}
          error={actionError?.message ?? null}
          onClose={() => setShowAdd(false)}
          onSubmit={async (body) => {
            const ok = await addEmployer(body);
            if (ok) setShowAdd(false);
          }}
        />
      )}
    </Card>
  );
}

function EmployerDialog({
  isWorking,
  error,
  onClose,
  onSubmit,
}: {
  isWorking: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (body: EmployerInput) => void;
}) {
  const [name, setName] = useState('');
  const [ein, setEin] = useState('');
  const [state, setState] = useState('');
  const [esi, setEsi] = useState(false);
  return (
    <Dialog title="Add employer" onClose={onClose}>
      <label style={styles.field}>
        <span style={styles.label}>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} style={styles.input} />
      </label>
      <label style={styles.field}>
        <span style={styles.label}>EIN</span>
        <input value={ein} onChange={(e) => setEin(e.target.value)} style={styles.input} placeholder="00-0000000" />
      </label>
      <label style={styles.field}>
        <span style={styles.label}>State</span>
        <input value={state} onChange={(e) => setState(e.target.value.toUpperCase())} style={styles.input} maxLength={2} />
      </label>
      <label style={{ ...styles.field, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <input type="checkbox" checked={esi} onChange={(e) => setEsi(e.target.checked)} />
        <span style={styles.label}>Offers employer-sponsored insurance</span>
      </label>
      {error && <p style={styles.error}>{error}</p>}
      <DialogActions
        isWorking={isWorking}
        disabled={!name.trim()}
        submitLabel="Add"
        onClose={onClose}
        onSubmit={() => onSubmit({ name: name.trim(), ein: ein.trim() || null, state: state.trim() || null, offers_esi: esi })}
      />
    </Dialog>
  );
}

// --- State Directory (ADH-006) ---------------------------------------------

function StatesTab() {
  const { stateContacts, stateContactsAvailable, isLoading, isWorking, actionError, refresh, addStateContact } =
    useAdminCatalog();
  const [showAdd, setShowAdd] = useState(false);

  const columns: Column<StateContact>[] = [
    { key: 'state', header: 'State', render: (s) => <strong>{s.state}</strong> },
    { key: 'program', header: 'Program', render: (s) => s.program },
    { key: 'contact', header: 'Contact', render: (s) => s.contact_name ?? '—' },
    { key: 'phone', header: 'Phone', render: (s) => <span style={styles.mono}>{s.phone ?? '—'}</span> },
    {
      key: 'portal',
      header: 'Portal',
      render: (s) =>
        s.portal_url ? (
          <a href={s.portal_url} target="_blank" rel="noreferrer" style={styles.link}>
            link ↗
          </a>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <Card
      title="State contact directory"
      actions={
        <>
          {!stateContactsAvailable && <span style={styles.seedTag}>seed</span>}
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
          <button type="button" style={styles.primaryBtnSm} onClick={() => setShowAdd(true)}>
            + Add contact
          </button>
        </>
      }
      padded={false}
    >
      <DataTable columns={columns} rows={stateContacts} rowKey={(s) => s.id} emptyTitle="No state contacts" />
      {showAdd && (
        <StateDialog
          isWorking={isWorking}
          error={actionError?.message ?? null}
          onClose={() => setShowAdd(false)}
          onSubmit={async (body) => {
            const ok = await addStateContact(body);
            if (ok) setShowAdd(false);
          }}
        />
      )}
    </Card>
  );
}

function StateDialog({
  isWorking,
  error,
  onClose,
  onSubmit,
}: {
  isWorking: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (body: StateContactInput) => void;
}) {
  const [state, setState] = useState('');
  const [program, setProgram] = useState('');
  const [contact, setContact] = useState('');
  const [phone, setPhone] = useState('');
  const [portal, setPortal] = useState('');
  return (
    <Dialog title="Add state contact" onClose={onClose}>
      <label style={styles.field}>
        <span style={styles.label}>State</span>
        <input value={state} onChange={(e) => setState(e.target.value.toUpperCase())} style={styles.input} maxLength={2} />
      </label>
      <label style={styles.field}>
        <span style={styles.label}>Program</span>
        <input value={program} onChange={(e) => setProgram(e.target.value)} style={styles.input} />
      </label>
      <label style={styles.field}>
        <span style={styles.label}>Contact name</span>
        <input value={contact} onChange={(e) => setContact(e.target.value)} style={styles.input} />
      </label>
      <label style={styles.field}>
        <span style={styles.label}>Phone</span>
        <input value={phone} onChange={(e) => setPhone(e.target.value)} style={styles.input} />
      </label>
      <label style={styles.field}>
        <span style={styles.label}>Portal URL</span>
        <input value={portal} onChange={(e) => setPortal(e.target.value)} style={styles.input} />
      </label>
      {error && <p style={styles.error}>{error}</p>}
      <DialogActions
        isWorking={isWorking}
        disabled={!state.trim() || !program.trim()}
        submitLabel="Add"
        onClose={onClose}
        onSubmit={() =>
          onSubmit({
            state: state.trim(),
            program: program.trim(),
            contact_name: contact.trim() || null,
            phone: phone.trim() || null,
            portal_url: portal.trim() || null,
          })
        }
      />
    </Dialog>
  );
}

// --- PDF Field Mapping (ADH-004, INT-008) ----------------------------------

function PdfTab() {
  const { pdfMappings, pdfMappingsAvailable, isLoading, refresh } = useAdminCatalog();

  const columns: Column<PdfMapping>[] = [
    { key: 'state', header: 'State', render: (m) => <strong>{m.state}</strong> },
    { key: 'form', header: 'Form', render: (m) => <span style={{ fontWeight: 600 }}>{m.form_name}</span> },
    { key: 'fields', header: 'Mapped fields', align: 'right', render: (m) => m.field_count },
    {
      key: 'unmapped',
      header: 'Unmapped',
      align: 'right',
      render: (m) =>
        m.unmapped_count > 0 ? (
          <span style={{ color: colors.warning, fontWeight: 700 }}>{m.unmapped_count}</span>
        ) : (
          <span style={{ color: colors.success }}>0</span>
        ),
    },
    { key: 'version', header: 'Version', align: 'right', render: (m) => `v${m.version}` },
    { key: 'status', header: 'Status', render: (m) => <StatusBadge value={m.status} /> },
  ];

  return (
    <Card
      title="State PDF field mapping"
      actions={
        <>
          {!pdfMappingsAvailable && <span style={styles.seedTag}>seed</span>}
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        </>
      }
      padded={false}
    >
      <DataTable columns={columns} rows={pdfMappings} rowKey={(m) => m.id} emptyTitle="No PDF mappings" />
    </Card>
  );
}

// --- Email Templates (ADH-009) ---------------------------------------------

function TemplatesTab() {
  const { templates, templatesAvailable, deliveries, isLoading, refresh } = useNotifications();

  const usage = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of deliveries) map.set(d.template_id, (map.get(d.template_id) ?? 0) + 1);
    return map;
  }, [deliveries]);

  const emailTemplates = templates.filter((t) => t.channel === 'email' || t.channel === 'inbox');
  const rows = emailTemplates.length ? emailTemplates : templates;

  const columns: Column<MessageTemplate>[] = [
    {
      key: 'name',
      header: 'Template',
      render: (t) => (
        <div>
          <div style={{ fontWeight: 600 }}>{t.name || t.template_id}</div>
          {t.subject && <div style={styles.muted}>{t.subject}</div>}
        </div>
      ),
    },
    { key: 'channel', header: 'Channel', render: (t) => t.channel },
    {
      key: 'vars',
      header: 'Variables',
      render: (t) =>
        t.variables.length ? <span style={styles.mono}>{t.variables.map((v) => `{${v}}`).join(' ')}</span> : '—',
    },
    { key: 'version', header: 'Version', align: 'right', render: (t) => `v${t.version}` },
    { key: 'usage', header: 'Sends', align: 'right', render: (t) => usage.get(t.template_id) ?? 0 },
  ];

  return (
    <Card
      title="Email & message templates"
      actions={
        <>
          {!templatesAvailable && <span style={styles.seedTag}>seed</span>}
          <button type="button" className="btn btn-secondary btn-sm" onClick={refresh} disabled={isLoading}>
            Refresh
          </button>
        </>
      }
      padded={false}
    >
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(t) => t.template_id}
        emptyTitle="No templates"
        emptyDescription="Templates from the notifications engine appear here with usage counts."
      />
    </Card>
  );
}

// --- Shared dialog primitives ----------------------------------------------

function Dialog({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-label={title} onClick={onClose}>
      <div style={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <h3 style={styles.dialogTitle}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function DialogActions({
  isWorking,
  disabled,
  submitLabel,
  onClose,
  onSubmit,
}: {
  isWorking: boolean;
  disabled: boolean;
  submitLabel: string;
  onClose: () => void;
  onSubmit: () => void;
}) {
  return (
    <div style={styles.dialogActions}>
      <button type="button" style={styles.smallBtn} onClick={onClose} disabled={isWorking}>
        Cancel
      </button>
      <button type="button" style={styles.primaryBtn} disabled={isWorking || disabled} onClick={onSubmit}>
        {isWorking ? 'Saving…' : submitLabel}
      </button>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  muted: { color: colors.textSecondary, fontSize: '0.82rem' },
  mono: { fontFamily: 'monospace', fontSize: '0.8rem' },
  error: { color: colors.danger, fontSize: '0.9rem' },
  link: { color: colors.action, fontWeight: 600, textDecoration: 'none' },
  seedTag: {
    background: colors.brandTint,
    color: colors.brandActive,
    borderRadius: radius.pill,
    padding: '1px 8px',
    fontSize: '0.7rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  tabs: { display: 'flex', gap: 4, flexWrap: 'wrap' },
  tabBtn: {
    padding: '7px 14px',
    background: 'transparent',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.pill,
    fontSize: '0.85rem',
    fontWeight: 600,
    color: colors.textSecondary,
    cursor: 'pointer',
  },
  tabBtnActive: { background: colors.brandTint, color: colors.brandActive, borderColor: colors.brandTint },
  simBody: { display: 'grid', gridTemplateColumns: 'minmax(280px, 380px) 1fr', gap: 24 },
  simControls: { display: 'flex', flexDirection: 'column', gap: 12 },
  subLabel: { fontSize: '0.82rem', margin: '0 0 8px', color: colors.textSecondary, fontWeight: 600 },
  decisionGrid: { display: 'flex', flexDirection: 'column', gap: 4, margin: '8px 0' },
  decisionRow: { display: 'flex', gap: 12, alignItems: 'baseline' },
  dt: { color: colors.textSecondary, fontSize: '0.84rem', minWidth: 150 },
  dd: { margin: 0, fontWeight: 600 },
  explanation: { margin: '8px 0', color: colors.textSecondary, fontStyle: 'italic' },
  miniTable: { width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', marginTop: 8 },
  miniTh: { textAlign: 'left', padding: '6px 8px', borderBottom: `2px solid ${colors.border}`, color: colors.textSecondary, fontWeight: 600, fontSize: '0.76rem' },
  miniTd: { padding: '6px 8px', borderBottom: `1px solid ${colors.border}` },
  field: { display: 'flex', flexDirection: 'column', gap: 4 },
  label: { fontSize: '0.82rem', fontWeight: 600, color: colors.textSecondary },
  input: { padding: '8px 10px', border: `1px solid ${colors.border}`, borderRadius: radius.sm, fontSize: '0.92rem' },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    resize: 'vertical',
  },
  smallBtn: {
    padding: '5px 12px',
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: radius.sm,
    fontSize: '0.82rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  dangerBtn: {
    padding: '4px 10px',
    background: colors.white,
    border: `1px solid ${colors.danger}`,
    color: colors.danger,
    borderRadius: radius.sm,
    fontSize: '0.78rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  primaryBtn: {
    padding: '7px 16px',
    background: colors.action,
    color: colors.white,
    border: 'none',
    borderRadius: radius.sm,
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  primaryBtnSm: {
    padding: '5px 12px',
    background: colors.action,
    color: colors.white,
    border: 'none',
    borderRadius: radius.sm,
    fontSize: '0.82rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28,25,23,0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
  },
  dialog: {
    background: colors.white,
    borderRadius: radius.lg,
    padding: 24,
    width: 'min(440px, 92vw)',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  dialogTitle: { margin: 0, fontSize: '1.1rem', fontWeight: 700 },
  dialogActions: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 },
};
